import { Minze, MinzeElement } from 'https://cdn.jsdelivr.net/npm/minze@1.10.1';

/**
 * AutoSuggestionsInput component for handling user input in the search field.
 * @extends MinzeElement
 */
class AutoSuggestionsInput extends MinzeElement {
  reactive = [['value', '']];

  watch = [['value', this.watchSearchTerm]];

  eventListeners = [
    ['input', 'input', this.handleInput],
    ['input', 'keydown', this.handleKeyDown],
    [window, 'autosuggestions:search-new-query', this.handleNewSearchQuery],
    [window, 'autosuggestions:redirectrule-updated', this.handleRedirectRule],
  ];

  debounceTimer = null;

  /**
   * Renders the input element.
   * @returns {string} HTML string for the input element.
   */
  html = () => `
    <form action="/search" method="get" role="search" class="search-form">
      <input type="text" placeholder="Search..." value="${this.value}" />
    </form>
  `;

  /**
   * Defines the CSS for the component.
   * @returns {string} CSS string for the component.
   */
  css = () => `
    :host {
      flex: 1 1 0%;
      min-width: 0px;
    }
    .search-form {
      width: 100%;
      display: flex;
    }
    input {
      width: 100%;
      height: 48px;
      padding: 0;
      color: #151515;
      font-size: 1rem;
      font-weight: 400;
      outline: none;
      border: none;
      background-color: transparent;
    }
    input::placeholder {
      color: #666;
    }
    input:focus::placeholder {
      color: #000;
    }
    input:focus {
      outline: none;
    }
    input:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `;

  /**
   * Watches for changes in the search term, updates input value and dispatches an event.
   * @param {string} newValue - The new search term value.
   * @param {string} oldValue - The previous search term value.
   * @param {string} key - The key of the reactive property.
   * @param {object} target - The target object.
   */
  watchSearchTerm(newValue, oldValue, key, target) {
    this.dispatch('autosuggestions:search-query-updated', newValue);
  }

  /**
   * Handles input changes and debounces the search term update.
   * @param {Event} event - The input event.
   */
  handleInput(event) {
    const value = event.target.value.trim();
    const debounceDelay = 300;

    clearTimeout(this.debounceTimer);

    this.debounceTimer = setTimeout(() => {
      if (value !== this.value) this.value = value;
    }, debounceDelay);
  }

  /**
   * Handles the search term update event.
   * @param {CustomEvent} event - The event containing the updated search term.
   */
  handleNewSearchQuery(event) {
    const searchTerm = event.detail;
    this.value = searchTerm;
    const inputElement = this.shadowRoot.querySelector('input');
    if (inputElement) {
      inputElement.value = searchTerm;
    }
  }

  /**
   * Handles the redirect rule update event.
   * @param {CustomEvent} event - The event containing the redirected URL.
   */
  handleRedirectRule(event) {
    this.redirectUrl = event.detail;
  }

  /**
   * Handles keydown events on the input.
   * @param {KeyboardEvent} event - The keydown event.
   */
  handleKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();

      // Update the search query immediately before navigation
      this.dispatch('autosuggestions:search-query-updated', this.value);

      // Add a small delay to ensure state updates are processed
      setTimeout(() => {
        if (this.redirectUrl) {
          window.location.href = new URL(
            this.redirectUrl,
            window.location.origin
          );
        } else {
          // Navigate to the search page with the current input value
          const searchInput = this.select('.search-form input');
          window.location.href = `/search?q=${searchInput.value}`;
        }
      }, 160);
    }
  }

  /**
   * Lifecycle method called when the component is ready.
   */
  onReady() {
    this.value = '';
  }
}

/**
 * AutoSuggestionsResults component for displaying search results.
 * @extends MinzeElement
 */
class AutoSuggestionsResults extends MinzeElement {
  attrs = ['data'];
  static observedAttributes = ['data'];

  reactive = [
    ['hasSearched', false],
    ['currentSearchInput', ''],
  ];

  watch = [['data', this.watchData]];

  /**
   * Watches for changes in the data property.
   * @param {Object|null} newValue - The new value of the data property.
   * @param {Object|null} oldValue - The previous value of the data property.
   */
  watchData(newValue, oldValue) {
    if (newValue) {
      // Perform actions with the new data
      this.processNewData(newValue);
    }
  }

  /**
   * Processes the new data.
   * @param {Object} newData - The new data to process.
   */
  processNewData(newData) {
    this.data = newData;
    this.hasSearched = true;
  }

  /**
   * Handles a query suggestion click.
   * @param {Event} event - The click event.
   */
  handleQuerySuggestion(event) {
    event.preventDefault();
    this.currentSearchInput = event.target.dataset.query;
    this.dispatch(
      'autosuggestions:search-new-query',
      event.target.dataset.query
    );
  }

  /**
   * Renders query suggestions.
   * @param {Object} result - The query suggestion data.
   * @returns {string} HTML string for the query suggestion.
   */
  renderQuerySuggestions(result) {
    const isActive = this.currentSearchInput === result.query;
    const activeClass = isActive ? 'active' : '';
    return `
      <a href="#" class="query-badge ${activeClass}" data-query="${result.query}" on:click="handleQuerySuggestion">
        ${result.displayText}
      </a>
    `;
  }

  /**
   * Renders a product card.
   * @param {Object} result - The product data.
   * @returns {string} HTML string for the product card.
   */
  renderProductCard(result) {
    const displayPrice = result.sale_price || result.price;
    const showCompareAtPrice =
      result.price !== undefined && result.sale_price !== result.price;

    return `
      <div class="result-item" data-id="${result.pid}">
        <div class="result-item__image">
          <img width="120" src="${decodeURIComponent(
            result.thumb_image
          )}" alt="${result.title}" />
        </div>
        <div class="result-item__content">
          <h3 class="result-item-title">
            <a class="result-item-link" title="${
              result.title
            }" href="${decodeURIComponent(result.url)}" aria-hidden="true"></a>
            ${result.title}
          </h3>
          <div class="price">
            <span class="current-price">$${displayPrice}</span>
            <span class="compare-at-price ${
              showCompareAtPrice ? '' : ' hidden'
            }">$${result.price}</span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Renders the empty state when no results are found.
   * @returns {string} HTML string for the empty state.
   */
  renderEmptyState() {
    if (this.hasSearched) {
      return `
        <div class="results-empty">
          <p>No results found. Try a different search term.</p>
        </div>
      `;
    }

    return '';
  }

  /**
   * Renders the component's HTML.
   * @returns {string} HTML string for the component.
   */
  html = () => {
    if (
      !this.data ||
      !this.data.products ||
      !this.data.products.docs ||
      this.data.products.docs?.length === 0
    ) {
      return this.renderEmptyState();
    }

    const searchSuggestions = this.data.products.docs || [];
    const querySuggestions =
      this.data.suggestionGroups[0]?.querySuggestions || [];

    return `
      <div class="suggestions">
      ${
        querySuggestions.length > 0
          ? `
        <div class="suggestion-group" data-group="query-suggestions">
          <h3 class="suggestion-group__title">Suggestions</h3>
          <div class="suggestion-group__content">
            <div class="scrollable-list">
              <div class="query-badge-list">
                ${querySuggestions
                  .map((result) => this.renderQuerySuggestions(result))
                  .join('')}
              </div>
            </div>
          </div>
        </div>
        `
          : ''
      }
      ${
        searchSuggestions.length > 0
          ? `
        <div class="suggestion-group" data-group="products">
          <h3 class="suggestion-group__title">Products</h3>
          <div class="suggestion-group__content search-results">
            ${searchSuggestions
              .map((result) => this.renderProductCard(result))
              .join('')}
          </div>
        </div>
        `
          : ''
      }
      </div>`;
  };

  /**
   * Defines the CSS for the component.
   * @returns {string} CSS string for the component.
   */
  css = () => `
    .search-results {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
    .result-item {
      position: relative;
      display: flex;
      align-items: flex-start;
    }
    .result-item__image {
      flex-shrink: 0;
    }
    .result-item__image img {
      width: 95px;
      height: auto;
    }
    .result-item__content {
      margin-left: 1.5rem;
    }
    .result-item-title {
      color: #151515;
      word-wrap: break-word;
      font-size: 15px;
      line-height: 22px;
      font-weight: 500;
      text-transform: uppercase;
    }
    .result-item-link {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
    }
    .results-empty {
      text-align: center;
      padding: 1rem;
      font-size: 14px;
      color: #666;
    }
    .price {
      margin-top: 3px;
      font-size: 14px;
      display: flex;
      font-weight: 400;
      letter-spacing: .02em;
    }
    .compare-at-price {
      margin-left: 0.5rem;
      opacity: .7;
      text-decoration: line-through;
    }
    .suggestions {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      align-items: flex-start;
    }
    .suggestions > :not([hidden]) ~ :not([hidden]) {
      border-top: 1px solid #ddd;
    }
    .suggestion-group {
      display: flex;
      flex-direction: column;
      width: 100%;
      padding: 0.75rem 1.5rem;
    }
    .suggestion-group__title {
      padding: 1rem 0;
      font-weight: 600;
      font-size: .75rem;
      text-transform: uppercase;
    }
    .scrollable-list {
      overflow-x: auto;
      scroll-behavior: smooth;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    .scrollable-list::-webkit-scrollbar {
      display: none;
    }
    .query-badge-list {
      display: flex;
      align-items: center;
      gap: 1rem;
      white-space: nowrap;
    }
    .query-badge {
      display: inline-flex;
      align-items: center;
      font-size: 0.75rem;
      font-weight: 600;
      line-height: 1rem;
      text-transform: uppercase;
      text-align: center;
      padding: 0.5rem 0.75rem;
      border-radius: 9999px;
      color: #333;
      background-color: #f7f7f7;
      box-shadow: inset 0 0 0 1px #e2e2e2;
      transition: all .15s cubic-bezier(.4,0,.2,1);
    }
    .query-badge:hover {
      background-color: #151515;
      box-shadow: inset 0 0 0 1px #151515;
      color: #fff;
    }
    .query-badge.active {
      background-color: #151515;
      box-shadow: inset 0 0 0 1px #151515;
      color: #fff;
    }
    .hidden {
      display: none;
    }
  `;
}

/**
 * AutoSuggestionsLoader component for displaying a loading spinner.
 * @extends MinzeElement
 */
class AutoSuggestionsLoader extends MinzeElement {
  /**
   * Renders the loader's HTML.
   * @returns {string} HTML string for the loader.
   */
  html = () => `
    <div class="spinner"></div>
  `;

  /**
   * Defines the CSS for the component.
   * @returns {string} CSS string for the component.
   */
  css = () => `
    .spinner {
      width: 40px;
      height: 40px;
      border: 4px solid #ccc;
      border-top-color: #000;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 2rem auto;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
}

/**
 * AutoSuggestionsSearch component for handling the search process.
 * @extends MinzeElement
 */
class AutoSuggestionsSearch extends MinzeElement {
  attrs = ['products-endpoint', 'autosuggestions-endpoint'];
  static observedAttributes = ['products-endpoint', 'autosuggestions-endpoint'];

  reactive = [
    ['loading', false],
    ['data', null],
  ];

  watch = [['data', this.sanitizeData]];

  eventListeners = [
    [window, 'autosuggestions:search-query-updated', this.handleSearchUpdated],
  ];

  sanitizeData(newData) {
    if (!newData) return;

    const sanitizeValue = (value) => {
      if (typeof value === 'string') {
        return value.replace(/'/g, '&apos;');
      }
      if (Array.isArray(value)) {
        return value.map((item) => sanitizeValue(item));
      }
      if (typeof value === 'object' && value !== null) {
        return Object.keys(value).reduce((acc, key) => {
          acc[key] = sanitizeValue(value[key]);
          return acc;
        }, {});
      }
      return value;
    };

    // Compare newData with the current data to avoid redundant updates.
    const sanitizedData = sanitizeValue(newData);
    if (JSON.stringify(this.data) === JSON.stringify(sanitizedData)) {
      return; // Skip update if the data hasn't changed.
    }

    // Update this.data only if it has changed.
    this.data = sanitizedData;
  }

  /**
   * Renders the component's HTML.
   * @returns {string} HTML string for the component.
   */
  html = () => `
    <div class="auto-suggest-search">
      <us-auto-suggestions-loader class="${
        this.loading ? '' : 'hidden'
      }"></us-auto-suggestions-loader>
      <us-auto-suggestions-results data='${JSON.stringify(this.data)}' class="${
    this.loading ? 'hidden' : ''
  }"></us-auto-suggestions-results>
    </div>
  `;

  /**
   * Defines the CSS for the component.
   * @returns {string} CSS string for the component.
   */
  css = () => `
    .auto-suggest-search {
      position: relative;
      width: 100%;
      height: 100%;
    }

    .hidden {
      display: none;
    }
  `;

  /**
   * Handles search query updates and fetches results.
   * @param {CustomEvent} event - The event containing the updated search query.
   */
  async handleSearchUpdated(event) {
    const searchTerm = event.detail;

    if (searchTerm.length < 2) {
      this.data = null;
      this.loading = false;
      return;
    }

    this.loading = true;
    const [autosuggestions, products] = await Promise.all([
      this.fetchAutosuggestions(searchTerm),
      this.fetchSearchResults(searchTerm),
    ]);

    this.data = {
      ...autosuggestions,
      products: products?.response,
    };

    this.loading = false;
  }

  async fetchAutosuggestions(searchTerm) {
    if (!this.autosuggestionsEndpoint) {
      console.error('No Autosuggestions endpoint found');
      return;
    }

    try {
      const client = new window.PersonalisationService(
        this.autosuggestionsEndpoint
      );
      const response = await client.getAutoSuggestions(searchTerm);
      return response;
    } catch (error) {
      this.dispatch('autosuggestions:search-error', { message: error.message });
    }
  }

  async fetchSearchResults(searchTerm) {
    if (!this.productsEndpoint) {
      console.error('No products endpoint found');
      return;
    }

    try {
      const params = {
        view_id: 'AU',
        fl: 'pid,title,brand,url,thumb_image,sale_price,price',
        rows: 6,
      };
      const client = new window.PersonalisationService(
        this.productsEndpoint,
        false
      );
      const response = await client.getProducts(searchTerm, params);
      if (response.keywordRedirect) {
        const redirectedUrl = response.keywordRedirect['redirected url'];
        this.dispatch('autosuggestions:redirectrule-updated', redirectedUrl);
      }
      return response;
    } catch (error) {
      this.dispatch('autosuggestions:redirectrule-error', {
        message: error.message,
      });
    }
  }
}

class SearchRecommendations extends MinzeElement {
  attrs = [
    'global-recommendation-endpoint',
    'global-recommendation-widget-id',
    'popular-searches',
  ];
  reactive = [['data', null]];

  onReactive() {
    this.fetchGlobalRecommendations();
  }

  async fetchGlobalRecommendations() {
    const params = {
      rows: 6,
      fields: 'pid,title,brand,url,thumb_image,sale_price,price',
    };
    const client = new window.PersonalisationService(
      this.globalRecommendationEndpoint,
      false
    );
    const response = await client.getGlobalRecommendations(
      this.globalRecommendationWidgetId,
      params
    );
    this.data = response;
  }

  /**
   * Renders popular searches.
   * @param {Object} result - The query suggestion data.
   * @returns {string} HTML string for the query suggestion.
   */
  renderPopularSearches(result) {
    return `
        <a href="/search?q=${encodeURIComponent(
          result.search_term
        )}" class="query-badge">
          ${result.search_term}
        </a>
      `;
  }

  /**
   * Renders a product card.
   * @param {Object} result - The product data.
   * @returns {string} HTML string for the product card.
   */
  renderProductCard(result) {
    const displayPrice = result.sale_price || result.price;
    const showCompareAtPrice =
      result.sale_price && result.sale_price !== result.price;

    return `
      <div class="product-card" data-id="${result.pid}">
        <div class="product-card__image">
          <img width="120" src="${result.thumb_image}" alt="${result.title}" />
        </div>
        <div class="product-card__content">
          <h3 class="product-card-title">
            <a class="product-card-link" title="${
              result.title
            }" href="${decodeURIComponent(result.url)}" aria-hidden="true"></a>
            ${result.title}
          </h3>
          <div class="price">
            <span class="current-price">$${displayPrice}</span>
            <span class="compare-at-price ${
              showCompareAtPrice ? '' : ' hidden'
            }">$${result.price}</span>
          </div>
        </div>
      </div>
    `;
  }

  css = () => `
    .suggestions {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      align-items: flex-start;
    }
    .suggestions > :not([hidden]) ~ :not([hidden]) {
      border-top: 1px solid #ddd;
    }
    .suggestion-group {
      display: flex;
      flex-direction: column;
      width: 100%;
      padding: 0.75rem 1.5rem;
    }
    .suggestion-group__title {
      padding: 1rem 0;
      font-weight: 600;
      font-size: .75rem;
      text-transform: uppercase;
    }
    .product-list {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      row-gap: 1.5rem;
      column-gap: 0.5rem;
    }
    .product-card {
      position: relative;
    }
    .product-card__image {
      position: relative;
      width: 100%;
      padding-bottom: calc(3 / 2 * 100%);
    }
    .product-card__image img {
      position: absolute;
      height: 100%;
      width: 100%;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      object-fit: cover;
    }
    .product-card__content {
      margin-top: 0.5rem;
    }
    .product-card-title {
      color: #151515;
      word-wrap: break-word;
      font-size: 12px;
      line-height: 18px;
      font-weight: 500;
      text-transform: uppercase;
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
    .product-card-link {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
    }
    .results-empty {
      text-align: center;
      padding: 1rem;
      font-size: 14px;
      color: #666;
    }
    .price {
      margin-top: 3px;
      font-size: 12px;
      display: flex;
      font-weight: 400;
      letter-spacing: .02em;
    }
    .compare-at-price {
      margin-left: 0.5rem;
      opacity: .7;
      text-decoration: line-through;
    }
    .scrollable-list {
      overflow-x: auto;
      scroll-behavior: smooth;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    .scrollable-list::-webkit-scrollbar {
      display: none;
    }
    .query-badge-list {
      display: flex;
      align-items: center;
      gap: 1rem;
      white-space: nowrap;
    }
    .query-badge {
      display: inline-flex;
      align-items: center;
      font-size: 0.75rem;
      font-weight: 600;
      line-height: 1rem;
      text-transform: uppercase;
      text-align: center;
      padding: 0.5rem 0.75rem;
      border-radius: 9999px;
      color: #333;
      background-color: #f7f7f7;
      box-shadow: inset 0 0 0 1px #e2e2e2;
      transition: all .15s cubic-bezier(.4,0,.2,1);
    }
    .query-badge:hover {
      background-color: #151515;
      box-shadow: inset 0 0 0 1px #151515;
      color: #fff;
    }
    .hidden {
      display: none;
    }
  `;

  html = () => {
    if (!this.data || !this.popularSearches) {
      return '';
    }

    const popularSearches = this.popularSearches || [];
    const trendingProducts = this.data.response.docs || [];

    return `
      <div class="suggestions">
        <div class="suggestion-group" data-group="query-suggestions">
          <h3 class="suggestion-group__title">Popular Searches</h3>
          <div class="suggestion-group__content">
            <div class="scrollable-list">
              <div class="query-badge-list">
                ${popularSearches
                  .map((result) => this.renderPopularSearches(result))
                  .join('')}
              </div>
            </div>
          </div>
        </div>
        <div class="suggestion-group" data-group="products">
          <h3 class="suggestion-group__title">Trending Now</h3>
          <div class="suggestion-group__content product-list">
            ${trendingProducts
              .map((result) => this.renderProductCard(result))
              .join('')}
          </div>
        </div>
      </div>`;
  };
}

/**
 * AutoSuggestionsFooter component for the footer of the search results.
 * @extends MinzeElement
 */
class AutoSuggestionsFooter extends MinzeElement {
  attrs = [['no-css-reset', '']];

  /**
   * Renders the component's HTML.
   * @returns {string} HTML string for the component.
   */
  html = () => `
    <div class="auto-suggestions-footer">
      <slot name="footer-content"></slot>
    </div>
  `;

  css = () => `
    :host {
      padding: 25px 30px;
      position: sticky;
      display: block;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: white;
      border-top: 1px solid #e2e2e2;
      z-index: 35;
    }
  `;
}

/**
 * AutoSuggestionsDrawer component for the main search drawer.
 * @extends MinzeElement
 */
class AutoSuggestionsDrawer extends MinzeElement {
  attrs = ['popular-searches'];

  eventListeners = [
    ['.auto-suggestions-close', 'click', this.handleClose],
    [window, 'autosuggestions:search-query-updated', this.handleNewSearchQuery],
  ];

  reactive = [
    ['autosuggestionsEndpoint', this.dataset.autosuggestionsEndpoint],
    ['productsEndpoint', this.dataset.productsEndpoint],
    ['globalRecommendationEndpoint', this.dataset.globalRecommendationEndpoint],
    ['globalRecommendationWidgetId', this.dataset.globalRecommendationWidgetId],
    ['searchQuery', null],
    ['hasSearched', false],
  ];

  get searchPathWithQuery() {
    return `/search?q=${encodeURIComponent(this.searchQuery)}`;
  }

  /**
   * Gets the API config as a stringified JSON.
   * @returns {string} Stringified API config.
   */
  get injectApiConfig() {
    return `autosuggestions-endpoint=${JSON.stringify(
      this.autosuggestionsEndpoint
    )} products-endpoint=${JSON.stringify(this.productsEndpoint)}`;
  }

  get injectGlobalRecommendationConfig() {
    return `global-recommendation-endpoint=${JSON.stringify(
      this.globalRecommendationEndpoint
    )} global-recommendation-widget-id=${JSON.stringify(
      this.globalRecommendationWidgetId
    )} popular-searches='${JSON.stringify(this.popularSearches)}'`;
  }

  handleNewSearchQuery(event) {
    this.searchQuery = event.detail;
    this.hasSearched = true;
  }

  /**
   * Renders the component's HTML.
   * @returns {string} HTML string for the component.
   */
  html = () => `
    <div class="auto-suggestions__header">
      <us-auto-suggestions-input></us-auto-suggestions-input>
      <button class="auto-suggestions-close" aria-label="Close">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 1L1 13M13 13L1 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      </button>
    </div>
    <div class="auto-suggestions__content">
      <us-auto-suggestions-search class="${this.hasSearched ? '' : 'hidden'}" ${
    this.injectApiConfig
  }></us-auto-suggestions-search>
      <us-search-recommendations class="${this.hasSearched ? 'hidden' : ''}" ${
    this.injectGlobalRecommendationConfig
  }></us-search-recommendations>
    </div>
    <us-auto-suggestions-footer class="${
      !this.hasSearched || !this.searchQuery ? 'hidden' : ''
    }">
      <a slot="footer-content" href="${
        this.searchPathWithQuery
      }" class="auto-suggestions-footer__link">
        <span>View all results</span>
      </a>
    </us-auto-suggestions-footer>
  `;

  /**
   * Handles closing the search drawer.
   */
  handleClose() {
    document.body.classList.remove('open-cc');
    const drawerEl = document.getElementById('Search-Drawer');
    if (drawerEl) {
      drawerEl.classList.remove('active');
    }
  }

  /**
   * Defines the CSS for the component.
   * @returns {string} CSS string for the component.
   */
  css = () => `
    :host {
      position: relative;
      height: 100%;
    }
    .auto-suggestions__header {
      position: sticky;
      top: 0;
      left: 0;
      z-index: 35;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #fff;
      border-bottom: 1px solid #E2E2E2;
      padding: 1rem 1rem 1rem 1.75rem;
    }
    .auto-suggestions__content {
      flex: 1 1 0%;
    }
    .auto-suggestions-close {
      margin-left: 1.5rem;
      display: flex;
      padding: 0.75rem;
      color: #666;
    }
    .auto-suggestions-close:active,
    .auto-suggestions-close:focus {
      color: #151515;
    }
    .auto-suggestions-footer__link {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 48px;
      padding: 7px 30px;
      font-size: .875rem;
      font-weight: 600;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      text-align: center;
      color: #fff;
      background: #151515;
      border: 1px solid #151515;
      cursor: pointer;
      position: relative;
      z-index: 1;
      overflow: hidden;
      appearance: none;
      transition: color .25s cubic-bezier(.104, .204, .492, 1),
                  background-color .25s cubic-bezier(.104, .204, .492, 1),
                  border-color .25s cubic-bezier(.104, .204, .492, 1);
    }
    .auto-suggestions-footer__link::before {
      content: "";
      position: absolute;
      top: 0;
      left: -5%;
      width: 125%;
      height: 100%;
      background: var(--bg-body);
      z-index: 2;
      transform: rotate3d(0, 0, 1, 6deg) translate3d(-1.2em, 110%, 0);
      transform-origin: bottom left;
      transition: transform .25s cubic-bezier(.104, .204, .492, 1);
      will-change: transform;
    }
    .auto-suggestions-footer__link:hover {
      color: #151515;
    }
    .auto-suggestions-footer__link:hover::before {
      transform: rotate3d(0, 0, 1, 0) translateZ(0);
    }
    .auto-suggestions-footer__link span {
      position: relative;
      z-index: 3;
    }
    .hidden {
      display: none;
    }
  `;
}

/**
 * QuickSearchButton component for the search button in the header.
 * @extends MinzeElement
 */
class QuickSearchButton extends MinzeElement {
  /**
   * Renders the component's HTML.
   * @returns {string} HTML string for the component.
   */
  html = () => `
    <a on:click="handleClick" href="/search" title="Search">
      <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M13 13.5L17.7495 18.5M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round">
        </path>
      </svg>
    </a>
  `;

  /**
   * Defines the CSS for the component.
   * @returns {string} CSS string for the component.
   */
  css = () => `
    :host {
      color: #fff;
      height: 100%;
      display: inline-flex;
      align-items: center;
      padding: 0 7px;
    }
    @media (min-width: 769px) {
      :host {
        padding: 0 1rem;
      }
    }
    @media only screen and (min-width: 1068px) {
      :host {
        padding: 0 10px;
      }
    }
    svg {
      display: inline-block;
      vertical-align: middle;
    }
  `;

  /**
   * Toggles the search drawer.
   */
  toggleDrawer() {
    document.body.classList.toggle('open-cc');
    const drawerEl = document.getElementById('Search-Drawer');
    if (drawerEl) {
      drawerEl.classList.toggle('active');
    }
  }

  /**
   * Handles click events on the search button.
   * @param {Event} event - The click event.
   */
  handleClick(event) {
    event.preventDefault();
    this.toggleDrawer();
  }
}

// Component definitions
Minze.define('us-quick-search-button', QuickSearchButton);
Minze.define('us-auto-suggestions-drawer', AutoSuggestionsDrawer);
Minze.define('us-auto-suggestions-input', AutoSuggestionsInput);
Minze.define('us-auto-suggestions-results', AutoSuggestionsResults);
Minze.define('us-auto-suggestions-search', AutoSuggestionsSearch);
Minze.define('us-search-recommendations', SearchRecommendations);
Minze.define('us-auto-suggestions-footer', AutoSuggestionsFooter);
Minze.define('us-auto-suggestions-loader', AutoSuggestionsLoader);
